import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { LegalRequirementFormModal } from '../features/legalRequirements/LegalRequirementFormModal';
import {
  createEvaluation,
  escalateEvaluationToNc,
  fetchLegalRequirement,
  updateLegalRequirement,
} from '../features/legalRequirements/legalRequirementsApi';
import type { ComplianceStatus, LegalRequirementDetail } from '../features/legalRequirements/types';
import {
  COMPLIANCE_STATUS_LABEL,
  LEGAL_REQ_TYPE_LABEL,
  complianceStatusBadgeClass,
} from '../features/legalRequirements/legalRequirementsUtils';
import { DateCell } from '../components/DateCell';

const EVAL_STATUSES = Object.keys(COMPLIANCE_STATUS_LABEL) as ComplianceStatus[];

export function LegalRequirementDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { canEdit } = usePlatformAccessLevel('legal_requirements.access');
  const [item, setItem] = useState<LegalRequirementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [evalStatus, setEvalStatus] = useState<ComplianceStatus>('evaluando');
  const [evalEvidence, setEvalEvidence] = useState('');
  const [evalGaps, setEvalGaps] = useState('');
  const [evalDate, setEvalDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLegalRequirement(id);
      setItem(data);
    } catch {
      setError('No se pudo cargar el requisito legal.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleEvaluation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSubmitting(true);
    try {
      await createEvaluation(id, {
        evaluatedAt: new Date(evalDate).toISOString(),
        status: evalStatus,
        evidence: evalEvidence.trim() || null,
        gaps: evalGaps.trim() || null,
      });
      setEvalEvidence('');
      setEvalGaps('');
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = async (evalId: string) => {
    setSubmitting(true);
    try {
      await escalateEvaluationToNc(evalId);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="p-6 text-muted-foreground text-sm">Cargando…</p>;
  }

  if (error || !item) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-destructive">{error ?? 'Requisito no encontrado.'}</p>
        <Link to="/requisitos-legales" className="text-sm hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/requisitos-legales" className="text-sm text-muted-foreground hover:underline">
            ← Requisitos legales
          </Link>
          <h1 className="text-2xl font-bold mt-1">{item.title}</h1>
          <p className="text-muted-foreground text-sm font-mono">{item.code}</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-outline" onClick={() => setEditOpen(true)}>
            Editar
          </button>
        )}
      </div>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
          <h2 className="font-semibold">Datos generales</h2>
          <p>
            <span className="text-muted-foreground">Tipo:</span>{' '}
            {LEGAL_REQ_TYPE_LABEL[item.type]}
          </p>
          {item.issuingBody && (
            <p>
              <span className="text-muted-foreground">Emisor:</span> {item.issuingBody}
            </p>
          )}
          {item.reference && (
            <p>
              <span className="text-muted-foreground">Referencia:</span> {item.reference}
            </p>
          )}
          {item.processArea && (
            <p>
              <span className="text-muted-foreground">Área:</span> {item.processArea}
            </p>
          )}
          <p>
            <span className="text-muted-foreground">Alcance:</span> {item.scope}
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
          <h2 className="font-semibold">Cumplimiento y vencimientos</h2>
          <p>
            <span className="text-muted-foreground">Estado:</span>{' '}
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${complianceStatusBadgeClass(item.complianceStatus)}`}
            >
              {COMPLIANCE_STATUS_LABEL[item.complianceStatus]}
            </span>
          </p>
          <p>
            <span className="text-muted-foreground">Última evaluación:</span>{' '}
            {item.lastEvaluationDate ? <DateCell value={item.lastEvaluationDate} /> : '—'}
          </p>
          <p>
            <span className="text-muted-foreground">Próxima evaluación:</span>{' '}
            {item.nextEvaluationDate ? <DateCell value={item.nextEvaluationDate} /> : '—'}
          </p>
          <p>
            <span className="text-muted-foreground">Vigencia:</span>{' '}
            {item.effectiveDate ? <DateCell value={item.effectiveDate} /> : '—'}
          </p>
          <p>
            <span className="text-muted-foreground">Vencimiento:</span>{' '}
            {item.expiryDate ? <DateCell value={item.expiryDate} /> : '—'}
          </p>
          {item.alertEnabled && (
            <p>
              <span className="text-muted-foreground">Alerta:</span>{' '}
              {item.alertDaysBefore} día(s) antes
            </p>
          )}
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <h2 className="font-semibold">Evaluaciones de cumplimiento</h2>
        {item.evaluations.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin evaluaciones registradas.</p>
        ) : (
          <ul className="space-y-3">
            {item.evaluations.map((ev) => (
              <li key={ev.id} className="border border-border rounded-md p-3 text-sm space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <DateCell value={ev.evaluatedAt} />
                    <span
                      className={`ml-2 text-xs px-2 py-0.5 rounded-full ${complianceStatusBadgeClass(ev.status)}`}
                    >
                      {COMPLIANCE_STATUS_LABEL[ev.status]}
                    </span>
                  </div>
                  {canEdit && ev.status === 'no_cumple' && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={Boolean(ev.qmsRecordId) || submitting}
                      onClick={() => void handleEscalate(ev.id)}
                    >
                      {ev.qmsRecordId ? 'NC generada' : 'Generar NC'}
                    </button>
                  )}
                </div>
                {ev.evidence && (
                  <p>
                    <span className="text-muted-foreground">Evidencia:</span> {ev.evidence}
                  </p>
                )}
                {ev.gaps && (
                  <p>
                    <span className="text-muted-foreground">Brechas:</span> {ev.gaps}
                  </p>
                )}
                {ev.qmsRecordId && (
                  <p className="text-xs">
                    <Link
                      to={`/registros/no-conformidades/${ev.qmsRecordId}`}
                      className="text-primary hover:underline"
                    >
                      Ver no conformidad
                    </Link>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <form className="border-t border-border pt-4 space-y-3" onSubmit={(e) => void handleEvaluation(e)}>
            <h3 className="font-medium text-sm">Registrar evaluación</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="block text-sm">
                Fecha
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                  value={evalDate}
                  onChange={(e) => setEvalDate(e.target.value)}
                  required
                />
              </label>
              <label className="block text-sm">
                Resultado
                <select
                  className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                  value={evalStatus}
                  onChange={(e) => setEvalStatus(e.target.value as ComplianceStatus)}
                >
                  {EVAL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {COMPLIANCE_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              Evidencia
              <textarea
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[60px]"
                value={evalEvidence}
                onChange={(e) => setEvalEvidence(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Brechas / observaciones
              <textarea
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[60px]"
                value={evalGaps}
                onChange={(e) => setEvalGaps(e.target.value)}
              />
            </label>
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Registrar evaluación'}
            </button>
          </form>
        )}
      </section>

      <LegalRequirementFormModal
        open={editOpen}
        title="Editar requisito legal"
        initial={{
          title: item.title,
          type: item.type,
          issuingBody: item.issuingBody,
          reference: item.reference,
          scope: item.scope,
          processArea: item.processArea,
          effectiveDate: item.effectiveDate,
          expiryDate: item.expiryDate,
          alertEnabled: item.alertEnabled,
          alertDaysBefore: item.alertDaysBefore,
        }}
        onClose={() => setEditOpen(false)}
        onSubmit={async (payload) => {
          await updateLegalRequirement(id, payload);
          await load();
        }}
      />
    </div>
  );
}
